import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Euro, Tag, Rocket, Receipt, TrendingUp, Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function Revenue() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promo, setPromo] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/admin/revenue").then(({ data }) => setData(data)).finally(() => setLoading(false));
      api.get("/settings").then(({ data }) => setPromo(!!data.free_promo)).catch(() => {});
    } else if (user === false || (user && user.role !== "admin")) {
      setLoading(false);
    }
  }, [user]);

  const togglePromo = async (val) => {
    setPromo(val);
    try {
      await api.post("/admin/settings", { free_promo: val });
      toast.success(val ? "Gratis-actie ingeschakeld" : "Gratis-actie uitgeschakeld");
    } catch (e) {
      setPromo(!val);
      toast.error("Kon de actie niet bijwerken");
    }
  };

  if (user === null) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500">Laden...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const fmt = (n) => `€ ${Number(n || 0).toFixed(2).replace(".", ",")}`;

  const cards = [
    { label: "Totale omzet", value: fmt(data?.total_revenue), icon: TrendingUp, accent: true },
    { label: "Plaatsingskosten", value: fmt(data?.listing?.revenue), sub: `${data?.listing?.count || 0} advertenties`, icon: Tag },
    { label: "Boost-inkomsten", value: fmt(data?.boost?.revenue), sub: `${data?.boost?.count || 0} boosts`, icon: Rocket },
    { label: "Transacties", value: data?.total_transactions ?? 0, icon: Receipt },
  ];

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-1">
        <Euro className="h-4 w-4 text-rose-400" />
        <span className="font-mono text-xs uppercase tracking-widest text-rose-400">Beheer · Alleen voor eigenaar</span>
      </div>
      <h1 className="font-serif text-4xl font-semibold text-zinc-100 mb-8">Omzetoverzicht</h1>

      <div data-testid="promo-toggle-card" className="mb-8 flex items-center justify-between gap-4 rounded-xl border border-amber-800/40 bg-gradient-to-r from-amber-950/20 to-rose-950/20 px-5 py-4">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-amber-400 shrink-0" />
          <div>
            <div className="text-sm font-medium text-zinc-100">Lanceeractie — gratis plaatsen</div>
            <div className="text-xs text-zinc-500">Zet tijdelijk alle plaatsingskosten op €0 om snel meer aanbod aan te trekken.</div>
          </div>
        </div>
        <Switch data-testid="promo-toggle" checked={promo} onCheckedChange={togglePromo} className="data-[state=checked]:bg-amber-500" />
      </div>

      {loading ? (
        <div className="text-zinc-500">Laden...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {cards.map((c) => (
              <div key={c.label} data-testid={`revenue-${c.label}`} className={`rounded-xl border p-5 ${c.accent ? "border-rose-800/50 bg-rose-950/20" : "border-zinc-800 bg-zinc-900/40"}`}>
                <c.icon className={`h-5 w-5 mb-3 ${c.accent ? "text-rose-400" : "text-zinc-400"}`} />
                <div className={`font-mono text-2xl font-semibold ${c.accent ? "text-rose-300" : "text-zinc-100"}`}>{c.value}</div>
                <div className="text-xs text-zinc-500 mt-1">{c.label}</div>
                {c.sub && <div className="text-[11px] text-zinc-600 mt-0.5">{c.sub}</div>}
              </div>
            ))}
          </div>

          <h2 className="font-serif text-2xl text-zinc-200 mb-4">Omzet per type</h2>
          <div className="space-y-2 mb-10">
            {Object.entries(data?.by_type || {}).length === 0 && (
              <div className="text-sm text-zinc-500">Nog geen betalingen ontvangen.</div>
            )}
            {Object.entries(data?.by_type || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/40 px-4 py-3">
                <span className="text-sm text-zinc-300">{v.label}</span>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-zinc-500">{v.count}×</span>
                  <span className="font-mono text-rose-400">{fmt(v.revenue)}</span>
                </div>
              </div>
            ))}
          </div>

          <h2 className="font-serif text-2xl text-zinc-200 mb-4">Recente transacties</h2>
          <div className="rounded-xl border border-zinc-800 overflow-hidden">
            {(data?.recent || []).length === 0 ? (
              <div className="p-6 text-sm text-zinc-500">Nog geen transacties.</div>
            ) : (
              data.recent.map((t, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/60 last:border-0 bg-zinc-900/30">
                  <div>
                    <div className="text-sm text-zinc-200">{t.label}</div>
                    <div className="text-[11px] text-zinc-600">{t.date ? new Date(t.date).toLocaleString("nl-NL") : ""}</div>
                  </div>
                  <span className="font-mono text-emerald-400">+ {fmt(t.amount)}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
